import { useEffect, useState, useCallback } from 'react';
import { useDebounce } from 'use-debounce';

export interface FormData {
    programId: string;
    name: string;
    description: string;
    code: string;
    isTrial: boolean;
}

type UseTermFormProps = {
    initialFormData: FormData;
    debounceDelay?: number;
};

export function useSubjectForm({ initialFormData, debounceDelay = 300 }: UseTermFormProps) {
    // Form data state from the api call
    const [formData, setFormData] = useState<FormData>(initialFormData);

    // Raw form debouncing and data or the dirty form data once the user
    // filled out information in textfields
    const [rawFormData, setRawFormData] = useState<FormData>(initialFormData);
    const [debouncedFormData] = useDebounce(rawFormData, debounceDelay);

    // Touch fields recording to handle error textfield validation
    const [touchedFields, setTouchedFields] = useState<Record<keyof FormData, boolean>>({
        programId: false,
        name: false,
        description: false,
        code: false,
        isTrial: false,
    });

    // Debouncing mechanism for form state (create / edit)
    useEffect(() => {
        setFormData(debouncedFormData);
    }, [debouncedFormData]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { name, value, type, checked } = e.target as HTMLInputElement;
            setRawFormData((prev) => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value,
            }));
        },
        [],
    );

    const handleBlur = useCallback(
        (field: keyof FormData) => {
            setTouchedFields((prev) => ({ ...prev, [field]: true }));
        },
        [],
    );

    const resetForm = useCallback((newInitialData?: FormData) => {
        const resetData = newInitialData || initialFormData;
        setRawFormData(resetData);
        setFormData(resetData);
        setTouchedFields({
            programId: false,
            name: false,
            description: false,
            code: false,
            isTrial: false,
        });
    }, [initialFormData]);

    const isFormValid = useCallback(() => {
        return (
            !!formData.name &&
            !!formData.description &&
            !!formData.code &&
            formData.isTrial !== undefined
        );
    }, [formData]);

    return {
        formData,
        rawFormData,
        setRawFormData,
        setFormData,
        touchedFields,
        setTouchedFields,
        handleChange,
        handleBlur,
        resetForm,
        isFormValid,
    };
}
